<template>
    <button type="button" class="equipment-card" :class="[`equipment-card--${statusKey}`, { 'equipment-card--highlighted': highlighted && isCooking }]" @click="onClick">
        <template v-if="isCooking">
            <span class="equipment-card__main">
                <span class="equipment-card__status">{{ $t('phu_production.equipment.cooking') }}</span>
                <span class="equipment-card__image" :style="imageStyle" role="img" :aria-label="productName"></span>
                <span class="equipment-card__product">{{ productName }}</span>
                <span class="equipment-card__units">X{{ equipment.cooking.units }}</span>
            </span>
            <span class="equipment-card__timer">
                <span class="equipment-card__time">{{ countdown }}</span>
                <ProgressBar :items-completed="progress" :items-total="100" variant="cooking" />
            </span>
        </template>

        <template v-else>
            <i class="i-andy-xl equipment-card__icon" :class="isAvailable ? 'i-andy-done' : 'i-andy-close'" aria-hidden="true"></i>
            <span class="equipment-card__status">{{ statusLabel }}</span>
            <span class="equipment-card__hint">{{ hint }}</span>
        </template>
    </button>
</template>

<script>
import ProgressBar from '@/components/domain/ui/ProgressBar.vue'
import { EQUIPMENT_STATUS } from '@/store/static/phuProduction'
import { formatCountdown, elapsedPercent } from '../utils/time'

const STATUS_KEYS = {
    [EQUIPMENT_STATUS.UNAVAILABLE]: 'unavailable',
    [EQUIPMENT_STATUS.AVAILABLE]: 'available',
    [EQUIPMENT_STATUS.COOKING]: 'cooking'
}

export default {
    name: 'EquipmentCard',
    components: { ProgressBar },
    props: {
        equipment: { type: Object, required: true },
        product: { type: Object, default: null },
        now: { type: Number, required: true },
        highlighted: { type: Boolean, default: false }
    },
    computed: {
        statusKey() {
            if (this.equipment.status === EQUIPMENT_STATUS.UNAVAILABLE) {
                return STATUS_KEYS[EQUIPMENT_STATUS.UNAVAILABLE]
            }
            if (this.isCooking) {
                return STATUS_KEYS[EQUIPMENT_STATUS.COOKING]
            }
            return STATUS_KEYS[EQUIPMENT_STATUS.AVAILABLE]
        },
        isCooking() {
            return Boolean(this.equipment.cooking) && this.now < this.equipment.cooking.ends_at
        },
        isAvailable() {
            return this.statusKey === STATUS_KEYS[EQUIPMENT_STATUS.AVAILABLE]
        },
        productName() {
            return this.product ? this.product.name : ''
        },
        imageStyle() {
            if (!this.product || !this.product.image) {
                return {}
            }
            return { backgroundImage: `url(${this.product.image})` }
        },
        countdown() {
            return formatCountdown(this.equipment.cooking.ends_at - this.now)
        },
        progress() {
            return elapsedPercent(this.equipment.cooking.started_at, this.equipment.cooking.ends_at, this.now)
        },
        statusLabel() {
            return this.$t(`phu_production.equipment.${this.statusKey}`)
        },
        hint() {
            return this.$t(this.isAvailable ? 'phu_production.equipment.mark_unavailable' : 'phu_production.equipment.mark_available')
        }
    },
    methods: {
        onClick() {
            if (this.isCooking) {
                return
            }
            this.$emit('toggle', this.equipment)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.equipment-card {
    @include display-flex();
    @include flex-direction(column);
    @include align-items(center);
    @include justify-content(center);
    gap: $phu-gap;
    width: 100%;
    height: 100%;
    padding: $phu-gap;
    border: 2px solid transparent;
    border-radius: $phu-radius-s;
    background-color: $phu-surface;
    text-align: center;
    cursor: pointer;

    &__main {
        @include display-flex();
        @include flex-direction(column);
        @include align-items(center);
        @include justify-content(center);
        gap: 8px;
        flex: 1;
        width: 100%;
    }

    &__status {
        max-width: 100%;
        @include text-ellipsis();
        font-family: $text-bold;
        font-size: 1.5rem;
        line-height: normal;
        color: $neutro-s50;
    }

    &__product,
    &__units {
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 20px;
        text-transform: uppercase;
        color: $phu-text;
    }

    &__time {
        font-family: $text-bold;
        font-size: 0.875rem;
        line-height: 20px;
        color: $warning-dark;
    }

    &__image {
        width: 48px;
        height: 48px;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
    }

    &__timer {
        @include display-flex();
        @include flex-direction(column);
        @include align-items(center);
        width: 100%;
    }

    &__hint {
        font-family: $text;
        font-size: 1.25rem;
        line-height: normal;
        color: $phu-contrast-text;
        max-width: 100%;
        text-align: center;
    }

    &--cooking {
        @include justify-content(space-between);
        padding: 4px;
        border-color: $neutro-dark;
        cursor: default;

        .equipment-card__status {
            font-size: 1.125rem;
            color: $phu-warning;
        }
    }

    &--available {
        border-color: $phu-available-border;
        background-color: $phu-available-bg;

        .equipment-card__icon {
            color: $phu-success;
        }

    }

    &--highlighted {
        border-color: $phu-warning;
        animation: equipment-highlight 1000ms ease-in-out 3;
    }

    &--unavailable {
        border-width: 1px;
        border-color: $neutro-dark;
        background-color: $error-t90;

        .equipment-card__icon {
            color: $error;
        }

    }
}

@keyframes equipment-highlight {
    50% {
        box-shadow: 0 0 0 4px rgba($phu-warning, 0.35);
    }
}
</style>
